import { useEffect, useState } from "react";
import { Tax } from "../../../types";
import { fetchTaxes } from "../../../api";
import Select, { ActionMeta, MultiValue, SingleValue } from "react-select";
import CustomModal from "../../widgets/components/CustomModal";
import { TaxForm } from "../../settings/TaxSettings";
import { mergeSelectStyles } from "../../widgets/components/SelectStyles";



interface OptionType {
  value: string;
  label: string;
  taxPct?: number;
}

interface TaxPickerProps {
  label?: string;
  tabIndex?: number;
  onTaxChange: (taxId: string, taxPct: number) => void;
  selectedTax?: string;
  showIntraStatTax?: boolean;
  onTextInput?: (value: string) => void;
}

export function TaxPicker({
  label,
  tabIndex,
  onTaxChange,
  selectedTax,
  showIntraStatTax,
  onTextInput
}: TaxPickerProps) {
  const [taxOptions, setTaxOptions] = useState<OptionType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    const getTaxes = async () => {
      try {
        const fetchedTax: Tax[] = await fetchTaxes();

        const options: OptionType[] = fetchedTax
          .map(tax => ({
            value: tax.taxId || '',
            label: tax.taxName || '',
            taxPct: tax.taxPct || 0
          }));
        setTaxOptions(options);

        // Set the default selected contact if provided
        if (selectedTax) {
          const defaultOption = options.find(option => option.value === selectedTax) || { label: selectedTax, value: selectedTax, taxPct: undefined };

          setSelectedOption(defaultOption);
        }
      } catch (error) {
        console.error(`Error fetching Accounts:`, error);
      }
    };

    getTaxes();
  }, [selectedTax, triggerRefresh]);

  const customStyles = mergeSelectStyles({
    option: (provided: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
  });

  const formatOptionLabel = (option: OptionType) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {option.label}
      {option.taxPct !== undefined && option.taxPct !== null && (
        <span className="ms-1">({option.taxPct}%)</span>
      )}
    </div>
  );

  const handleSelectChange = (selectedOption: SingleValue<OptionType> | MultiValue<OptionType>, _actionMeta: ActionMeta<OptionType>) => {
    const option = selectedOption as SingleValue<OptionType>;
    if (option && option.value === 'add_new') {
      setIsModalOpen(true);
    } else {
      setSelectedOption(option);
      onTaxChange(option?.value || '', option?.taxPct || 0);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAddNew = (newOption: OptionType) => {
    setIsModalOpen(false);
    setTaxOptions(prevAccounts => [...prevAccounts, newOption]);
    setSelectedOption(newOption);
    onTaxChange(newOption.value, newOption.taxPct || 0);
  };

  const modifiedOptions = [
    { value: 'add_new', label: 'Add New'.toTranslatedDisplay(), taxPct: 0 },
    ...taxOptions,
  ];

  return (
    <div className="searchable-picker " style={{ minWidth: '120px' }}>
      {label && <label className="form-label">{label}</label>}
      <Select
        className="react-select-styled react-select"
        classNamePrefix="react-select"
        options={modifiedOptions}
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        placeholder={`${label || 'Tax'}`}
        value={selectedOption}
        onChange={handleSelectChange}
        tabIndex={tabIndex}
        onInputChange={(value, meta) => {
          if (meta.action === 'input-change') {
            setInputValue(value);
            const newTax = {
              value: value,
              label: value,
            };
            setSelectedOption(newTax);
            if(onTextInput) onTextInput(value)
          }
        }}
      />
      <CustomModal
        title={`${'Add New'.toTranslatedDisplay()} ${(label || 'Tax').toTranslatedDisplay()}`}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        size="large"
        body={<TaxForm onClose={() => { setIsModalOpen(false) }} onSave={() => setTriggerRefresh(!triggerRefresh)} />}
        id={''}
        onSave={() => { }}
      />
    </div>
  );


}